import { getEvoRoomCardItems } from '@/app/components/hotel-detail/evo/rooms/getEvoRoomCardItems';

type BoardClassTarget = { room: RoomType; mealPlan: MealPlanType };

// Resolves a room and meal plan for a board class: the given room is kept when it offers the board
// class; otherwise the cheapest room offering it wins — in both cases with the room's cheapest rate of
// that board class (the default cancellation option, matching the "from +€X" price the bar advertises).
// `configurations` is price-sorted ascending, so the first match is a room's cheapest such rate;
// across rooms, ties keep the first room in page order. The board class switch itself goes through
// resolveBoardClassSwitch below; this keep-or-cheapest rule also backs the meal-filter preselection.
export function selectBoardClassTarget(
  rooms: readonly RoomType[],
  boardClass: BoardClassType,
  selectedRoom: RoomType | undefined
): { room: RoomType; mealPlan: MealPlanType } | undefined {
  const cheapestOf = (room: RoomType) => room.configurations.find((config) => config.boardClass === boardClass);

  if (selectedRoom) {
    const mealPlan = cheapestOf(selectedRoom);
    if (mealPlan) return { room: selectedRoom, mealPlan };
  }

  let target: { room: RoomType; mealPlan: MealPlanType } | undefined;
  rooms.forEach((room) => {
    const mealPlan = cheapestOf(room);
    if (mealPlan && (!target || mealPlan.finalPrice < target.mealPlan.finalPrice)) {
      target = { room, mealPlan };
    }
  });
  return target;
}

// Across one or more board classes, returns the single cheapest room+rate. Used in evo to preselect
// the room/meal plan a meal-plan URL filter points at, without filtering the rooms list.
export function selectCheapestBoardClassTarget(
  rooms: readonly RoomType[],
  boardClasses: readonly BoardClassType[]
): { room: RoomType; mealPlan: MealPlanType } | undefined {
  let target: { room: RoomType; mealPlan: MealPlanType } | undefined;
  boardClasses.forEach((boardClass) => {
    const candidate = selectBoardClassTarget(rooms, boardClass, undefined);
    if (candidate && (!target || candidate.mealPlan.finalPrice < target.mealPlan.finalPrice)) {
      target = candidate;
    }
  });
  return target;
}

// MPH-1366: the room and rate of the first card the evo rooms list shows for a board class, which is the
// cheapest room with its cheapest rate of that board class. Built on the list's own sort, so the selected
// room is the first card by construction, ties (room name) and promotion groups included.
export function selectFirstRoomCardTarget(
  rooms: readonly RoomType[],
  boardClass: BoardClassType
): BoardClassTarget | undefined {
  const [firstItem] = getEvoRoomCardItems(rooms, boardClass);
  return firstItem ? { room: firstItem.room, mealPlan: firstItem.configurations[0] } : undefined;
}

// MPH-1366: resolves the selection when the user switches board class in the board class bar. A room the
// user picked is kept, with its cheapest rate of the new board class, when it offers that board class. Some
// suppliers list one room as several offers split by board class (same name, another roomId), so when the
// picked offer lacks the board class, the same-named offer that has it is kept as the user's room instead.
// In every other case — the page's own preselection, or a picked room that the new board class does not
// offer under any of its offers — the first card of the new tab is selected, and the result is the page's
// own choice again.
export function resolveBoardClassSwitch(params: {
  rooms: readonly RoomType[];
  boardClass: BoardClassType;
  selectedRoom: RoomType | undefined;
  isRoomPickedByUser: boolean;
}): (BoardClassTarget & { isRoomPickedByUser: boolean }) | undefined {
  const { rooms, boardClass, selectedRoom, isRoomPickedByUser } = params;

  if (isRoomPickedByUser && selectedRoom) {
    const mealPlan = selectedRoom.configurations.find((config) => config.boardClass === boardClass);
    if (mealPlan) return { room: selectedRoom, mealPlan, isRoomPickedByUser: true };

    // roomsIncludedInPrice too: on multi-room searches a room is listed once per rooms-included count (MPH-882).
    const sameRoomItem = getEvoRoomCardItems(rooms, boardClass).find(
      (item) =>
        item.room.name === selectedRoom.name && item.room.roomsIncludedInPrice === selectedRoom.roomsIncludedInPrice
    );
    if (sameRoomItem) {
      return { room: sameRoomItem.room, mealPlan: sameRoomItem.configurations[0], isRoomPickedByUser: true };
    }
  }

  const target = selectFirstRoomCardTarget(rooms, boardClass);
  return target ? { ...target, isRoomPickedByUser: false } : undefined;
}
